import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getPublicConfig, type PublicConfig } from "./api";

type AuthStatus = "loading" | "signed_out" | "authenticated" | "preview";

type AuthContextValue = {
  status: AuthStatus;
  config: PublicConfig | null;
  accessToken: string | null;
  email: string | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  enterPreview: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [client, setClient] = useState<SupabaseClient | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    void getPublicConfig()
      .then(async (nextConfig) => {
        if (!active) return;
        setConfig(nextConfig);

        if (!nextConfig.supabaseUrl || !nextConfig.supabasePublishableKey) {
          if (nextConfig.previewEnabled) {
            setStatus("signed_out");
            return;
          }
          throw new Error("Supabase public configuration is not set.");
        }

        const nextClient = createClient(
          nextConfig.supabaseUrl,
          nextConfig.supabasePublishableKey,
          {
            auth: {
              persistSession: true,
              autoRefreshToken: true,
              detectSessionInUrl: true,
            },
          },
        );
        setClient(nextClient);

        const { data, error: sessionError } = await nextClient.auth.getSession();
        if (!active) return;
        if (sessionError) throw sessionError;

        const session = data.session;
        setAccessToken(session?.access_token ?? null);
        setEmail(session?.user.email ?? null);
        setStatus(session ? "authenticated" : "signed_out");

        const listener = nextClient.auth.onAuthStateChange((_event, nextSession) => {
          setAccessToken(nextSession?.access_token ?? null);
          setEmail(nextSession?.user.email ?? null);
          setStatus(nextSession ? "authenticated" : "signed_out");
        });
        unsubscribe = () => listener.data.subscription.unsubscribe();
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(reason instanceof Error ? reason.message : "Could not open AutoSocial.");
        setStatus("signed_out");
      });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const signIn = useCallback(
    async (nextEmail: string, password: string) => {
      if (!client) throw new Error("Secure login is still starting. Please try again.");
      setError(null);
      const { data, error: signInError } = await client.auth.signInWithPassword({
        email: nextEmail,
        password,
      });
      if (signInError) {
        setError(signInError.message);
        throw signInError;
      }
      if (!data.session) throw new Error("Supabase did not return a session.");
    },
    [client],
  );

  const signOut = useCallback(async () => {
    if (status === "preview") {
      setStatus("signed_out");
      setEmail(null);
      return;
    }
    await client?.auth.signOut();
  }, [client, status]);

  const enterPreview = useCallback(() => {
    if (!config?.previewEnabled) return;
    setEmail("Preview — no live account");
    setStatus("preview");
  }, [config]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, config, accessToken, email, error, signIn, signOut, enterPreview }),
    [accessToken, config, email, enterPreview, error, signIn, signOut, status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
